import { tinycolor, SVG, $, _ } from "js/vendor";
import React from "reactn";
import log from "js/core/logger";
import { timeoutRace, loadImage } from "js/core/utilities/promiseHelper";
import { ASSET_LOAD_TIMEOUT, AssetType } from "common/constants";
import { getCSSTransform } from "js/core/utilities/geom";
import { getElementTransition, SVGGroup, ELEMENT_TRANSITION_DURATION } from "../../core/utilities/svgHelpers";
import { renderAdjustmentFilter, renderColorFilter } from "../../core/utilities/svgFilters";
import { MediaElement } from "./MediaElement";
import { ds } from "js/core/models/dataService";

class Picture extends MediaElement {
    async _load() {
        if (this.asset && this.hasMedia && this.assetId == this.asset.id) return;

        if (this.assetId) {
            try {
                const asset = await ds.assets.getAssetById(this.assetId, AssetType.IMAGE);
                asset.resizeIfNeeded();
                this.assetUrl = await asset.getURL(this.initialSizeToLoadMediaAt);
                this.asset = asset;

                this.isOptimalSizeLoaded = false;
                this.hasMedia = true;
            } catch (err) {
                log.error("Failed to load asset " + this.assetId + " : " + err);
                this.hasMedia = false;
            }
        } else {
            this.asset = null;
            this.hasMedia = false;
        }
    }

    renderChildren(props, transition) {
        if (this.asset) {
            // If the optimal size image hasn't been loaded, load it and then refresh to render
            if (!this.isOptimalSizeLoaded && !window.isRenderer && !this.canvas.isExport) {
                // Setting isOptimalSizeLoaded to true immediately to avoid multiple loadOptimalSize() runs
                this.isOptimalSizeLoaded = true;
                this.canvas.layouter.runPostRender(() => {
                    if (transition) {
                        // We want to load the optimal size only after the transition is finished to avoid
                        // breaking the animation when the optimal sized image is loaded and rendered
                        _.delay(() => this.loadOptimalSize(), ELEMENT_TRANSITION_DURATION);
                    } else {
                        this.loadOptimalSize();
                    }
                });
            }

            return this.renderImage(props, transition);
        } else {
            return (
                <SVGGroup key={this.id}>
                    <g id="imageNode"></g>
                </SVGGroup>
            );
        }
    }

    renderImage(props, transition) {
        let { transformProps } = props;

        let imageFilterId;
        let colorFilter;
        if (props.filter && props.filter != "None") {
            if (this.colorFilters.includes(props.filter)) {
                colorFilter = renderColorFilter(this, props, this.canvas.getTheme());
                imageFilterId = `url(#${this.uniqueId}-color-filter)`;
            } else {
                imageFilterId = `url(#${props.filter})`;
            }
        }

        let adjustmentFilter;
        if (props.filterBrightness || props.filterBlur || props.filterContrast) {
            adjustmentFilter = renderAdjustmentFilter(this, props);
        }

        // create a unique ID to help apply styling
        const hasImageFilter = !!imageFilterId;
        const filterId = hasImageFilter ? `filter_${Date.now().toString("16")}` : undefined;

        return (
            <React.Fragment key={this.id}>
                {/* Safari struggles respecting the attribute change, but a unique style each time works well */}
                {hasImageFilter && (
                    <style type="text/css" >{`
                        #${filterId} {
                            filter: ${imageFilterId};
                        }
                    `}</style>
                )}
                <SVGGroup overflowHidden>
                    <g id="imageNode"
                        filter={adjustmentFilter ? `url(#${this.uniqueId}-adj-filter)` : null}
                        style={{ transform: getCSSTransform(transformProps), transition: getElementTransition(transition) }}
                    >
                        {adjustmentFilter}
                        {colorFilter}
                        <image
                            id={filterId}
                            href={this.assetUrl}
                            width={this.mediaSize.width}
                            height={this.mediaSize.height}
                            style={{ imageOrientation: "from-image" }}
                            onLoad={this.getImageOnLoadPromiseResolver(this.assetUrl)}
                        />
                    </g>
                </SVGGroup>
            </React.Fragment>
        );
    }

    loadImageFromURL(url, size) {
        const asset = this.asset;
        return timeoutRace(ASSET_LOAD_TIMEOUT, loadImage(url)
            .catch(() => {
                log.info("Could not load image, attempting to cache bust asset url for " + asset.id);
                return asset.getURL(size, true).then(url => loadImage(url));
            }));
    }
}

class Logo extends Picture {
    get isTransparent() {
        return this.asset.get("hasAlpha");
    }

    get backgroundColor() {
        // let color = super.backgroundColor;
        // if (color){
        //     return color;
        // } else {
        if (this.asset.get("hasAlpha")) {
            return "none";
        } else {
            return "white";
        }
        // }
    }

    get constrainAspectRatio() {
        return "fit";
    }

    minScale(size) {
        return this.calcFitMedia(size, 0.5).scale;
    }

    maxScale(size) {
        return this.calcFitMedia(size, 1).scale;
    }

    getDefaultMediaTransform(size) {
        return this.calcFitMedia(size, this.options.defaultScale || 0.9);
    }
}

class LogoXXX extends Picture {
    // get backgroundColor() {
    //     if (this.isTransparent) {
    //         return "none";
    //     } else {
    //         return "white";
    //     }
    // }

    get _doubleClickToSelect() {
        return true;
    }

    get _canSelect() {
        return false;
    }

    get isTransparent() {
        return this.asset.get("hasAlpha");
    }

    getScale(size) {
        return size.width / this.mediaSize.width;
    }

    // temp hack until logos are trimmed at import time because we need the inital size and optimal size to be the same
    // or the trimmed size changes
    get initialSizeToLoadMediaAt() {
        return "original";
    }

    calculateOptimalAssetSizeType() {
        return "original";
    }

    //     mediaSize {
    //         return this.trimmedSize;
    //     }

    //     loadImageFromURL(url, size) {
    //         return super.loadImageFromURL(url, size).then(img => {
    //             return new Promise(resolve => {
    //                 let image = new Image();
    //                 image.onload = () => {
    //                     this.trimmedSize = new geom.Size(image.naturalWidth, image.naturalHeight);
    //                     resolve(image);
    //                 };
    //                 image.src = trimWhiteEdges(img);
    //             });
    //         }
    //         );
    //     }
}

export { Picture, Logo };
